import { forwardRef, useImperativeHandle, useRef } from "react";

export interface SelectionBandHandle {
  /** Fractions of the plot width, ascending, or null to clear. */
  setRange: (range: [number, number] | null) => void;
}

/**
 * The translucent band a zoom-to-selection drag paints.
 *
 * Imperative, like the axis preview beside it: a drag writes a new range every pointermove, and
 * routing that through React state would re-render the whole chart — series, scales and all — on
 * every frame of a gesture whose entire job is to stay smooth.
 */
export const SelectionBand = forwardRef<
  SelectionBandHandle,
  { leftPx: number; topPx: number; widthPx: number; heightPx: number }
>(function SelectionBand({ leftPx, topPx, widthPx, heightPx }, ref) {
  const groupRef = useRef<SVGGElement>(null);
  const rectRef = useRef<SVGRectElement>(null);
  const startRef = useRef<SVGLineElement>(null);
  const endRef = useRef<SVGLineElement>(null);

  useImperativeHandle(ref, () => ({
    setRange: (range) => {
      const g = groupRef.current;
      if (!g) return;
      if (!range || range[1] - range[0] <= 0) {
        g.setAttribute("opacity", "0");
        return;
      }
      g.setAttribute("opacity", "1");
      const x0 = leftPx + range[0] * widthPx;
      const x1 = leftPx + range[1] * widthPx;
      rectRef.current?.setAttribute("x", String(x0));
      rectRef.current?.setAttribute("width", String(x1 - x0));
      startRef.current?.setAttribute("x1", String(x0));
      startRef.current?.setAttribute("x2", String(x0));
      endRef.current?.setAttribute("x1", String(x1));
      endRef.current?.setAttribute("x2", String(x1));
    },
  }));

  // The band is bounded left and right only: the edges are what the drag is choosing, and a box
  // drawn around the full plot height reads as a region lifted out of the chart rather than as a
  // start and an end marked on the time axis.
  return (
    <g ref={groupRef} opacity={0} pointerEvents="none">
      <rect
        ref={rectRef}
        x={leftPx}
        y={topPx}
        width={0}
        height={heightPx}
        fill="var(--color-viz-categorical-1)"
        fillOpacity={0.12}
      />
      {[startRef, endRef].map((edgeRef, index) => (
        <line
          // biome-ignore lint/suspicious/noArrayIndexKey: two fixed edges, start then end
          key={index}
          ref={edgeRef}
          x1={leftPx}
          x2={leftPx}
          y1={topPx}
          y2={topPx + heightPx}
          stroke="var(--color-viz-categorical-1)"
          strokeOpacity={0.9}
          strokeWidth={1}
          strokeDasharray="4 3"
        />
      ))}
    </g>
  );
});
